import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Checkbox, FormControlLabel, Link, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { APP_ROUTES_ATTRIBUTES } from 'constants/routes';
import { useLowResolution } from 'hooks';
import React, { FC } from 'react';
import { IWizardChecklistItem } from 'types/wizard';
import { useHover } from 'usehooks-ts';
import { CHECKLIST_LINKS, WIZARD_CHECKLIST_ITEM_DONE } from '../utils/consts';

interface IWizardChecklistItemProps {
	name: string;
	status: string;
	onClick: (item: IWizardChecklistItem) => void;
}

const WizardChecklistItem: FC<IWizardChecklistItemProps> = ({
	name,
	status,
	onClick
}) => {
	const hoverRef = React.useRef(null);
	const isHover = useHover(hoverRef);
	const lowRes = useLowResolution();

	const [currentStatus, setCurrent] = React.useState(status);

	let checkboxTitle = '';
	const checked = WIZARD_CHECKLIST_ITEM_DONE === currentStatus;
	if (!checked) {
		checkboxTitle = intl.tr(
			'wizard.phase.customize.checklist.item.check.done.icon'
		);
	} else {
		checkboxTitle = intl.tr(
			'wizard.phase.customize.checklist.item.uncheck.done.icon'
		);
	}

	const linkTitle = intl.tr('wizard.phase.customize.checklist.item.icon');
	const url = CHECKLIST_LINKS[name] || 'https://docs.descope.com';
	const pageTitle = APP_ROUTES_ATTRIBUTES[url]?.labelId;

	let color = checked ? 'default' : 'inherit';
	if (isHover) {
		color = 'primary';
	}

	const handleClick = React.useCallback(() => {
		const newStatus =
			currentStatus === WIZARD_CHECKLIST_ITEM_DONE
				? ''
				: WIZARD_CHECKLIST_ITEM_DONE;
		setCurrent(newStatus);
		onClick({
			name,
			status: newStatus
		});
	}, [currentStatus, name, onClick]);

	const content = (
		<>
			<Typography
				data-testid="wizard-checklist-item-name"
				variant="body1"
				sx={{
					textDecoration: checked ? 'line-through' : ''
				}}
			>
				{intl.tr(`wizard.checklist.item.${name}`)}
			</Typography>
			{isHover && (
				<Box pl={1} data-testid="wizard-checkbox-open-icon">
					<OpenInNewIcon color="primary" fontSize="small" />
				</Box>
			)}
		</>
	);

	const label =
		lowRes || checked ? (
			content
		) : (
			<Link
				title={`${linkTitle} ${pageTitle ? intl.tr(pageTitle) : url}`}
				href={url}
				ref={hoverRef}
				display="flex"
				flexDirection="row"
				underline="hover"
				color={color}
				target="_blank"
				data-testid="wizard-checklist-link"
			>
				{content}
			</Link>
		);

	return (
		<Box display="flex" flexDirection="row" flexGrow={1} alignItems="center">
			<FormControlLabel
				data-testid={`wizard-checkbox-status-${name}`}
				control={
					<Checkbox
						title={checkboxTitle}
						aria-label={checkboxTitle}
						checked={checked}
						color={isHover ? 'primary' : 'default'}
						onChange={handleClick}
						data-testid="wizard-checkbox-status"
					/>
				}
				label={label}
			/>
		</Box>
	);
};

export default WizardChecklistItem;
